import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { TableRowProps } from '@mui/material/TableRow'
import { CompetitionEntryDocument } from '@rallycry/api-suite-typescript/dist/models/CompetitionEntryDocument'
import { JoinRestriction } from '@rallycry/api-suite-typescript/dist/models/JoinRestriction'
import { TeamIssueTooltip } from './TeamIssueTooltip'
import { TeamCountDisplay } from './TeamCountDisplay'
import { RcIcon } from '@/components/atoms/RcIcon'
import { RcTrans } from '@/components/atoms/RcTrans'
import { Trophy } from '@/components/organisms/site/Trophy'
import { DiscoverCard } from '@/components/organisms/card/DiscoverCard'
import { CompetitionRoute, RootRoute } from '@/core/route-keys'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useNavigation } from '@/core/hooks/useNavigation'
import { useFeatures } from '@/components/providers/site/FeatureProvider'
import { EllipsisBox } from '@/components/molecules/text/EllipsisBox'

export interface TeamCardProps extends TableRowProps {
  team: CompetitionEntryDocument
  rank?: number
}

export const TeamCard = ({ team }: TeamCardProps) => {
  const { featHideCommunity } = useFeatures()
  const { getPath } = useNavigation()
  const { competition, settings, isSoloCompetition, isCompetitionModerator } =
    useCompetition()

  const image = team?.image || ''
  const teamPath = getPath({
    root: RootRoute.Competition,
    rootId: competition?.id,
    subRoute: CompetitionRoute.Team,
    subId: team?.id
  })

  return (
    <DiscoverCard
      fab={
        <Stack direction='row' spacing={3} alignItems='center'>
          {team?.lookingForMore ? (
            <Tooltip
              title={<RcTrans i18nKey='competition:team.looking-for-more' />}
            >
              <span>
                <RcIcon icon={['fal', 'flag-pennant']} color='primary' />
              </span>
            </Tooltip>
          ) : null}
          {team?.rank && team?.rank < 5 ? (
            <Trophy placement={team.rank} size={'xs'} />
          ) : null}
          {isCompetitionModerator ? (
            <TeamIssueTooltip teamId={team.id!} />
          ) : null}
        </Stack>
      }
      navigation={teamPath}
      avatarImage={image}
      title={team?.alternateName}
      content={
        <Stack direction='column' spacing={1}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <TeamCountDisplay
              current={team.playersCount}
              min={settings?.teamSizeMin}
              max={settings?.teamSizeMax}
            />
          </Stack>
          {team?.representing?.name && !featHideCommunity ? (
            <Stack
              direction='row'
              alignItems='flex-start'
              spacing={1}
              color='text.secondary'
            >
              <EllipsisBox lineClamp={1}>
                <Tooltip
                  title={
                    <RcTrans
                      i18nKey='shared.representing'
                      tOptions={{ name: team?.representing?.name }}
                    />
                  }
                >
                  <Typography variant='subtitle1' color='text.secondary'>
                    {team?.representing?.name}
                  </Typography>
                </Tooltip>
              </EllipsisBox>
            </Stack>
          ) : null}
          {isSoloCompetition ? null : (
            <Stack
              direction='row'
              alignItems='flex-start'
              spacing={1}
              color='text.secondary'
            >
              <RcIcon
                icon={
                  team?.joinRestriction === JoinRestriction.INVITE_ONLY
                    ? ['fal', 'lock']
                    : ['fal', 'unlock']
                }
                size='sm'
              />
              <Typography variant='subtitle1' color='text.secondary' pt={0.6}>
                {team?.joinRestriction === JoinRestriction.INVITE_ONLY ? (
                  <RcTrans i18nKey='competition:restriction.invite-only' />
                ) : (
                  <RcTrans i18nKey='competition:restriction.option-invite' />
                )}
              </Typography>
            </Stack>
          )}
        </Stack>
      }
    />
  )
}
